import { useCallback, useEffect, useRef, useState } from 'react';
import { PLAY_STEP_MS } from './metaHackathon-constants';

export interface TimelinePlayback {
  cursor: number; // float in [0, dateCount - 1]
  dateIndex: number; // rounded cursor — the "current" date
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  setDateIndex: (index: number) => void; // manual scrub (pauses playback)
}

/**
 * Drives smooth, looping timeline playback decoupled from React's render
 * cadence. `cursor` advances via requestAnimationFrame at one date per
 * `PLAY_STEP_MS`, wrapping around at the end so playback loops.
 */
export const useTimelinePlayback = (dateCount: number): TimelinePlayback => {
  const [cursor, setCursor] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const frameRef = useRef<number | undefined>(undefined);
  const lastTimeRef = useRef<number | undefined>(undefined);

  const play = useCallback(() => {
    if (dateCount > 1) {
      setIsPlaying(true);
    }
  }, [dateCount]);
  const pause = useCallback(() => setIsPlaying(false), []);
  const toggle = useCallback(
    () => setIsPlaying(prev => (dateCount > 1 ? !prev : false)),
    [dateCount]
  );

  const setDateIndex = useCallback(
    (index: number) => {
      setIsPlaying(false);
      const max = Math.max(0, dateCount - 1);
      setCursor(Math.min(max, Math.max(0, index)));
    },
    [dateCount]
  );

  // Keep the cursor in range if the dataset size changes underneath us.
  useEffect(() => {
    const max = Math.max(0, dateCount - 1);
    setCursor(prev => Math.min(prev, max));
  }, [dateCount]);

  useEffect(() => {
    if (!isPlaying || dateCount <= 1) return undefined;

    const loopLength = dateCount - 1;
    const step = (time: number) => {
      const last = lastTimeRef.current;
      lastTimeRef.current = time;
      if (last !== undefined) {
        const delta = (time - last) / PLAY_STEP_MS;
        setCursor(prev => (prev + delta) % (loopLength + 1));
      }
      frameRef.current = requestAnimationFrame(step);
    };

    frameRef.current = requestAnimationFrame(step);
    return () => {
      if (frameRef.current !== undefined) {
        cancelAnimationFrame(frameRef.current);
      }
      lastTimeRef.current = undefined;
    };
  }, [isPlaying, dateCount]);

  return {
    cursor,
    dateIndex: Math.round(cursor) % Math.max(1, dateCount),
    isPlaying,
    play,
    pause,
    toggle,
    setDateIndex
  };
};
